import { Injectable, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ExtensionTypes, NullActionTypes, Versions } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { trimPhoneNumber } from 'src/app/common-components/helpers/trim-phone-number-helper';
import { coerceToNullableNumber } from 'src/app/common-components/helpers/type-helper';
import { combinedAreaCodeValidator } from 'src/app/common-components/validators/area-code.validator';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import {
   nullableSignedIntegerValidators,
   unsignedNonZeroIntegerValidators,
} from 'src/app/common-components/validators/signed-integer.validator';
import { AccountExtensionView } from '../../add-account/add-account-extension/models/account-extension-view.model';
import { AccountPhoneNumberView } from '../../add-account/add-account-phone-numbers/models/account-phone-number-view.model';
import { ExtensionDetailSaveModel } from '../../extension-details/models/extension-detail-save.model';
import { LocationDetailFormValue } from '../../location-details/models/location-detail-form';
import { LocationDetailSaveModel } from '../../location-details/models/location-detail-save.model';
import { PhoneNumberDetailSaveModel } from '../../phone-number-details/models/phone-number-detail-save.model';
import { AccountDetailForm, AccountDetailFormValue } from '../models/account-detail-form';
import { AccountDetailSaveModel } from '../models/account-detail-save.model';
import { GetAccountDetail_account } from '../queries/generated/GetAccountDetail';

@Injectable()
export class AccountDetailFormService extends FormsBase implements OnDestroy {
   private destroyed$ = new Subject<void>();

   constructor(fb: UntypedFormBuilder, private store: Store, private action: Actions) {
      super();

      const form: AccountDetailForm = {
         activeEndDate: fb.control(null),
         activeStartDate: fb.control(null),
         callRetentionPolicyTypeId: fb.control(null),
         fileAttachment: fb.control(null),
         holdMusicPrompt: fb.control(null),
         isInternationalDialingEnabled: fb.control(null),
         isInternationalDialingSuspended: fb.control(null),
         isRecordInternalCalls: fb.control(null),
         isRecordInboundCalls: fb.control(null),
         isRecordOutboundCalls: fb.control(null),
         isCallRecordingFeatureEnabled: fb.control(true),
         isMailboxMessageTranscriptionEnabled: fb.control(null),
         isResidential: fb.control(null),
         isSuspended: fb.control(null),
         lcsAccountNumber: fb.control(null, nullableSignedIntegerValidators()),
         logLevelId: fb.control(null),
         faxFileTypeId: fb.control(null),
         name: fb.control(null, [Validators.pattern(/.*\S.*/), containsCharactersValidator()]),
         nameDirectoryPreferenceId: fb.control(null),
         operatorExtension: fb.control(null),
         pstnLineLimit: fb.control(null, unsignedNonZeroIntegerValidators()),
         shortName: fb.control(null, [Validators.pattern(/^[\s]*[a-z0-9]*[\s]*$/), Validators.maxLength(50)]),
         timeZoneId: fb.control(null),
         isOtrEnabled: fb.control(null),
         outboundRecordingNotificationEnabled: fb.control(null),
         defaultAreaCode: fb.control(null, combinedAreaCodeValidator()),
         isMissedCallsShown: fb.control(null),
         reasonLogLevelChanged: fb.control(null, containsCharactersValidator()),
         logLevelChanged: fb.control(null),
         isFmFmEnabled: fb.control(null),
         isLcsInternal: fb.control(null),
         note: fb.control(null),
         noteId: fb.control(null),
         schedule: fb.control(null),
         queueManagementStrategyTypeId: fb.control(null),
         isAgentCanAssign: fb.control(null),
         presenceEnabled: fb.control(null),
         hasPresenceCredentials: fb.control(null),
      };

      this.form = fb.group({
         ...form,
         newAccountLocations: fb.control([]),
         newExtensions: fb.control([]),
         newPhoneNumbers: fb.control([]),
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   toggleSystemFields(enabled: boolean, canEditAccountSettings: boolean): void {
      const activeStartDateControl = this.form.get('activeStartDate');
      const activeEndDateControl = this.form.get('activeEndDate');
      const lcsAccountNumberControl = this.form.get('lcsAccountNumber');
      const isMailboxMessageTranscriptionEnabledControl = this.form.get('isMailboxMessageTranscriptionEnabled');
      const isFmFmEnabledControl = this.form.get('isFmFmEnabled');
      const callRetentionPolicyTypeIdControl = this.form.get('callRetentionPolicyTypeId');
      const nameControl = this.form.get('name');
      const pstnLineLimit = this.form.get('pstnLineLimit');
      const isSuspendedControl = this.form.get('isSuspended');
      const isInternationalDialingEnabledControl = this.form.get('isInternationalDialingEnabled');
      const isRecordInboundCallsControl = this.form.get('isRecordInboundCalls');
      const isRecordOutboundCallsControl = this.form.get('isRecordOutboundCalls');
      const isRecordInternalCallsControl = this.form.get('isRecordInternalCalls');
      const isOtrEnabled = this.form.get('isOtrEnabled');

      const isPresenceEnabledControl = this.form.get('presenceEnabled');
      if (enabled && canEditAccountSettings) {
         activeStartDateControl?.enable();
         activeEndDateControl?.enable();
         lcsAccountNumberControl?.enable();
         isMailboxMessageTranscriptionEnabledControl?.enable();
         isFmFmEnabledControl?.enable();
         callRetentionPolicyTypeIdControl?.enable();
         nameControl?.enable();
         pstnLineLimit?.enable();
         isSuspendedControl?.enable();
         isInternationalDialingEnabledControl?.enable();
         isRecordInboundCallsControl?.enable();
         isRecordOutboundCallsControl?.enable();
         isRecordInternalCallsControl?.enable();
         isOtrEnabled?.enable();
         isPresenceEnabledControl?.enable();
      } else {
         activeStartDateControl?.disable();
         activeEndDateControl?.disable();
         lcsAccountNumberControl?.disable();
         isMailboxMessageTranscriptionEnabledControl?.disable();
         isFmFmEnabledControl?.disable();
         callRetentionPolicyTypeIdControl?.disable();
         nameControl?.disable();
         pstnLineLimit?.disable();
         isSuspendedControl?.disable();
         isInternationalDialingEnabledControl?.disable();
         isRecordInboundCallsControl?.disable();
         isOtrEnabled?.disable();
         isRecordOutboundCallsControl?.disable();
         isRecordInternalCallsControl?.disable();
         isPresenceEnabledControl?.disable();
      }
   }

   updateAccount(account: GetAccountDetail_account): void {
      if (!account) {
         return;
      }

      const formValue: Partial<AccountDetailFormValue> = {
         activeEndDate: account.activeEndDate,
         activeStartDate: account.activeStartDate,
         callRetentionPolicyTypeId: { callRetentionPolicy: account.callRetentionPolicyTypeId ?? null },
         fileAttachment: null,
         holdMusicPrompt: account.holdMusicPrompt ?? null,
         holdMusicPromptId: account.holdMusicPrompt?.promptId ?? null,
         isInternationalDialingEnabled: account.isInternationalDialingEnabled,
         isInternationalDialingSuspended: account.isInternationalDialingSuspended,
         isCallRecordingFeatureEnabled: account.isCallRecordingFeatureEnabled,
         isRecordInternalCalls: account.isRecordInternalCalls,
         isRecordInboundCalls: account.isRecordInboundCalls,
         isRecordOutboundCalls: account.isRecordOutboundCalls,
         isMailboxMessageTranscriptionEnabled: account.isMailboxMessageTranscriptionEnabled,
         isResidential: account.isResidential,
         isSuspended: account.isSuspended,
         lcsAccountNumber: account.lcsAccountNumber,
         logLevelId: { logLevelId: account.logLevelId ?? null },
         faxFileTypeId: { faxFileTypeId: account.faxFileTypeId ?? null },
         name: account.name,
         shortName: account.shortName,
         nameDirectoryPreferenceId: { nameDirectoryPreference: account.nameDirectoryPreferenceId ?? null },
         operatorExtension: account.operatorExtension ?? null,
         operatorExtensionId: account.operatorExtension?.extensionId ?? null,
         timeZoneId: { timeZoneId: account.timeZoneId ?? null },
         pstnLineLimit: account.pstnLineLimit,
         isOtrEnabled: account.isOtrEnabled,
         outboundRecordingNotificationEnabled: account.outboundRecordingNotificationEnabled,
         defaultAreaCode: account.defaultAreaCode,
         isMissedCallsShown: account.isMissedCallsShown,
         reasonLogLevelChanged: account.reasonLogLevelChanged,
         logLevelChanged: account.logLevelChanged,
         isFmFmEnabled: account.isFmFmEnabled,
         isLcsInternal: account.isLcsInternal,
         note: account.note?.notes ?? null,
         noteId: account.note?.noteId ?? null,
         scheduleId: account.schedule?.scheduleId ?? null,
         schedule: account.schedule ?? null,
         queueManagementStrategyTypeId: {
            queueManagementStrategyTypeId: account.queueManagementStrategyTypeId ?? null,
         },
         isAgentCanAssign: account.isAgentCanAssign,
         presenceEnabled: account.presenceEnabled,
         hasPresenceCredentials: account.hasPresenceCredentials,
      };

      this.form.patchValue(formValue);
      this.markAsUntouchedAndPristine();
   }

   getValue(accountId: number | null): AccountDetailSaveModel {
      const value = this.form.value as AccountDetailFormValue;

      const returnValue: AccountDetailSaveModel = {
         accountId,
         activeEndDate: value.activeEndDate,
         activeStartDate: value.activeStartDate,
         callRetentionPolicyTypeId: value.callRetentionPolicyTypeId?.callRetentionPolicy ?? null,
         holdMusicPromptId: value.holdMusicPrompt?.promptId ?? null,
         isInternationalDialingEnabled: value.isInternationalDialingEnabled,
         isInternationalDialingSuspended: value.isInternationalDialingSuspended,
         isCallRecordingFeatureEnabled: value.isCallRecordingFeatureEnabled,
         isRecordInternalCalls: value.isRecordInternalCalls,
         isRecordInboundCalls: value.isRecordInboundCalls,
         isRecordOutboundCalls: value.isRecordOutboundCalls,
         isMailboxMessageTranscriptionEnabled: value.isMailboxMessageTranscriptionEnabled,
         isResidential: value.isResidential,
         isSuspended: value.isSuspended,
         lcsAccountNumber: coerceToNullableNumber(value.lcsAccountNumber),
         logLevelId: value.logLevelId?.logLevelId ?? null,
         faxFileTypeId: value.faxFileTypeId?.faxFileTypeId ?? null,
         name: value.name,
         nameDirectoryPreferenceId: value.nameDirectoryPreferenceId?.nameDirectoryPreference ?? null,
         operatorExtensionId: value.operatorExtension?.extensionId ?? null,
         pstnLineLimit: value.pstnLineLimit ?? null,
         shortName: value.shortName,
         timeZoneId: value.timeZoneId?.timeZoneId ?? null,
         isOtrEnabled: value.isOtrEnabled,
         outboundRecordingNotificationEnabled: value.outboundRecordingNotificationEnabled,
         defaultAreaCode: value.defaultAreaCode,
         isMissedCallsShown: value.isMissedCallsShown,
         reasonLogLevelChanged: value.reasonLogLevelChanged,
         logLevelChanged: value.logLevelChanged,
         versionId: Versions.NDT2,
         isFmFmEnabled: value.isFmFmEnabled,
         isLcsInternal: value.isLcsInternal,
         note: {
            fields: [],
            noteId: value.noteId ?? 0,
            accountId: accountId ?? 0,
            notes: value.note ?? null,
            isSystemNote: true,
         },
         scheduleId: value.schedule?.scheduleId ?? null,
         queueManagementStrategyTypeId: value.queueManagementStrategyTypeId?.queueManagementStrategyTypeId ?? null,
         isAgentCanAssign: value.isAgentCanAssign,
         presenceEnabled: value.presenceEnabled,
      };
      if (!accountId) {
         if (value.newAccountLocations) {
            returnValue.newAccountLocations = value.newAccountLocations.map((location) => {
               return this.getAccountLocationSaveModel(location, accountId);
            });
         }

         returnValue.newExtensions = value.newExtensions?.map((extension) => {
            return this.getAccountExtensionSaveModel(extension, accountId);
         });

         returnValue.newPhoneNumbers = value.newPhoneNumbers?.map((phoneNumber) => {
            return this.getAccountPhoneNumberSaveModel(phoneNumber, accountId);
         });
      }
      return returnValue;
   }

   private getAccountLocationSaveModel(
      location: LocationDetailFormValue,
      accountId: number | null
   ): LocationDetailSaveModel {
      const locationDetail: LocationDetailSaveModel = {
         accountId: accountId,
         name: location.name ?? null,
         city: location.city ?? null,
         comment: location.comment ?? null,
         country: location.country ?? null,
         directionalPostfix: location.directionalPostfix ?? null,
         directionalPrefix: location.directionalPrefix ?? null,
         houseNumber: location.houseNumber ?? null,
         postalCode: location.postalCode ?? null,
         state: location.state ?? null,
         streetName: location.streetName ?? null,
         streetNameSuffix: location.streetNameSuffix ?? null,
         timeZoneId: location.timeZone?.timeZoneId ?? null,
         locationId: location.locationId,
      };

      return locationDetail;
   }

   private getAccountPhoneNumberSaveModel(
      phoneNumber: AccountPhoneNumberView,
      accountId: number | null
   ): PhoneNumberDetailSaveModel {
      const phoneNumberDetail: PhoneNumberDetailSaveModel = {
         phoneNumberId: phoneNumber.phoneNumberId,
         displayName: phoneNumber.displayName,
         phoneNumber: trimPhoneNumber(phoneNumber.phoneNumber),
         accountId: accountId ?? 0,
         routeTo: phoneNumber.routeTo?.number?.toString() ?? null,
         isActive: phoneNumber.isActive ?? true,
         isPorted: phoneNumber.isPorted ?? undefined,
         datePorted: phoneNumber.datePorted,
         providerId: phoneNumber.providerId ?? undefined,
      };

      return phoneNumberDetail;
   }

   private getAccountExtensionSaveModel(
      extension: AccountExtensionView,
      accountId: number | null
   ): ExtensionDetailSaveModel {
      const extensionDetail: ExtensionDetailSaveModel = {
         extension: {
            name: extension.name,
            number: extension.number,
            type: ExtensionTypes[extension.type.name],
            accountId: accountId ?? 0,
            extensionId: null,
            faxEmailAddresses: [],
            timeBasedRoutes: [],
            departmentExtensions: [],
         },
      };

      switch (extensionDetail.extension.type) {
         case ExtensionTypes.AutoAttendant:
            extensionDetail.extension.extensionAutoAttendant = {
               autoAttendantKeys: extension.autoAttendantKeys ?? [],
               autoAttendantPrompts: [],
               extensionAutoAttendantId: null,
               extensionId: extensionDetail.extension.extensionId,
               firstPromptNotSkippable: false,
               timeoutSeconds: extension.timeout,
               useTimeoutToggle: false,
               timeoutExtensionId: null,
               timeoutNullAction: NullActionTypes.Unset,
               isTimeoutVoicemailOnly: false,
            };
            break;
         case ExtensionTypes.HuntGroup:
            extensionDetail.extension.extensionHuntGroup = {
               callerIdOverride: false,
               displayName: null,
               extensionHuntGroupId: null,
               extensionHuntGroups: [],
               extensionId: extensionDetail.extension.extensionId,
               huntGroupMembers: [],
               ringTimeOut: extension.timeout,
            };
            break;
         case ExtensionTypes.SmartVoicemail:
         case ExtensionTypes.VoiceMailbox:
            extensionDetail.extension.mailbox = {
               emailAddress: extension.email,
               discardAudioAfterEmail: false,
               sayCallerId: true,
               markReadAfterEmail: false,
               isInNameDirectory: false,
               mailboxId: null,
               password: null,
            };
            extensionDetail.extension.extensionMailbox = {
               extensionId: extensionDetail.extension.extensionId,
               extensionMailboxId: null,
            };
            break;
         case ExtensionTypes.Fax:
            extensionDetail.extension.extensionFax = {
               defaultPhoneNumberId: null,
               emailAddress: extension.email,
               extensionFaxId: null,
               extensionId: null,
               faxEmailAddresses: null,
            };
            break;
         case ExtensionTypes.ExternalForward:
            extensionDetail.extension.extensionExternalForward = {
               extensionExternalForwardId: null,
               extensionId: extensionDetail.extension.extensionId,
               dtmf: null,
               forwardToNumber: trimPhoneNumber(extension.forwardTo),
               isDtmf: null,
               isPassthroughOriginalCallerId: null,
               pauseSeconds: null,
            };
            break;
      }

      return extensionDetail;
   }
}
